// web/src/contexts/SubscriptionContext.tsx
// Provides subscription status across the app for checking before actions

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import api from '../services/api';

export type SubscriptionExpiredReason =
  | 'subscription_expired'
  | 'trial_expired'
  | 'subscription_suspended'
  | 'subscription_canceled'
  | 'pending_approval';

export interface SubscriptionStatus {
  isActive: boolean;
  isTrial: boolean;
  trialExpired: boolean;
  trialDaysRemaining: number;
  trialEndDate?: string;
  reason?: SubscriptionExpiredReason;
  planName?: string;
  planSlug?: string;
}

interface SubscriptionContextType {
  subscriptionStatus: SubscriptionStatus | null;
  isLoading: boolean;
  refreshStatus: () => Promise<void>;
  // Check if action is allowed - returns true if allowed, shows alert and returns false if not
  checkCanPerformAction: (actionType: 'scan' | 'schedule' | 'general') => boolean;
  // Get message for blocked action
  getBlockedMessage: () => string;
}

const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

// Optional hook that returns null if not in context (for components that might be outside provider)
export const useSubscriptionOptional = () => {
  return useContext(SubscriptionContext);
};

interface SubscriptionProviderProps {
  children: React.ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSubscriptionStatus = useCallback(async () => {
    // Only fetch for client users (not superusers/admins)
    if (!user || user.role === 'superuser') {
      setSubscriptionStatus(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const response = await api.get('/subscription/limits');
      const data = response.data;

      // Determine the subscription state
      let reason: SubscriptionExpiredReason | undefined;
      let isActive = true;

      if (data.trial_expired) {
        reason = 'trial_expired';
        isActive = false;
      } else if (data.plan_slug === '' || !data.plan_name) {
        // No subscription found - might be pending approval
        reason = 'pending_approval';
        isActive = false;
      }

      // Calculate trial days remaining from trial_end
      let trialDaysRemaining = 0;
      let trialEndDate: string | undefined;
      if (data.trial_end) {
        const trialEnd = new Date(data.trial_end);
        trialEndDate = trialEnd.toLocaleDateString();
        const now = new Date();
        const diffTime = trialEnd.getTime() - now.getTime();
        trialDaysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (trialDaysRemaining < 0) trialDaysRemaining = 0;
      }

      setSubscriptionStatus({
        isActive: isActive && !data.trial_expired,
        isTrial: data.is_trial || false,
        trialExpired: data.trial_expired || false,
        trialDaysRemaining: trialDaysRemaining,
        trialEndDate: trialEndDate,
        reason: reason,
        planName: data.plan_name,
        planSlug: data.plan_slug,
      });
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
      // Don't block the UI if subscription check fails - assume active
      setSubscriptionStatus(null);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSubscriptionStatus();
    // Re-check every 5 minutes in case subscription status changes
    const interval = setInterval(fetchSubscriptionStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchSubscriptionStatus]);

  const getBlockedMessage = useCallback((): string => {
    if (!subscriptionStatus || subscriptionStatus.isActive) {
      return '';
    }

    switch (subscriptionStatus.reason) {
      case 'trial_expired':
        return t('subscriptions.enforcement.trialExpiredMessage',
          'Your trial has expired. Please upgrade to continue using all features.');
      case 'subscription_expired':
        return t('subscriptions.enforcement.expiredCannotScan',
          'Your subscription has expired. Please upgrade to create or start scans.');
      case 'subscription_suspended':
        return t('subscriptions.enforcement.contactSupport',
          'Your subscription has been suspended. Please contact support for assistance.');
      case 'subscription_canceled':
        return t('subscriptions.canceledNotice',
          'Your subscription has been canceled. Please reactivate to continue.');
      case 'pending_approval':
        return t('subscriptions.pendingApprovalMessage',
          'Your account is pending approval. Some features may be restricted.');
      default:
        return t('subscriptions.enforcement.expiredCannotScan',
          'Your subscription has expired. Please upgrade to continue.');
    }
  }, [subscriptionStatus, t]);

  const checkCanPerformAction = useCallback((actionType: 'scan' | 'schedule' | 'general'): boolean => {
    // Superusers or no status (fallback) - allow
    if (!subscriptionStatus) {
      return true;
    }

    // If subscription is active, allow
    if (subscriptionStatus.isActive) {
      return true;
    }

    // Subscription is not active - show alert and return false
    const message = getBlockedMessage();

    // Use browser alert for now - could be replaced with a modal/toast
    alert(message);

    return false;
  }, [subscriptionStatus, getBlockedMessage]);

  const value = useMemo<SubscriptionContextType>(() => ({
    subscriptionStatus,
    isLoading,
    refreshStatus: fetchSubscriptionStatus,
    checkCanPerformAction,
    getBlockedMessage,
  }), [subscriptionStatus, isLoading, fetchSubscriptionStatus, checkCanPerformAction, getBlockedMessage]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
